import { Link } from 'react-router'
import { BackLink } from '#app/components/back-link.tsx'
import { DemoCapsule } from '#app/features/portfolio/components/demo-capsule.tsx'
import { ImmutableBook } from './immutable-book.tsx'
import { MutableBook } from './mutable-book.tsx'

// Ported 2026-08-05 from the retired product's marketing site (its
// /ledger-demo page), where it was about to go offline — reframed from
// product pitch to Writing. The interactive halves (immutable-book,
// mutable-book) came across intact; the CTA and product framing did not.
// The Sacks-tweet hook and the how-I-found-this narrative moved to the
// find-your-passion essay, which links here — this page opens straight
// on the two-books tension it exists to demonstrate.
export function ImmutableLedgerDemo() {
	return (
		<main className="font-poppins mx-auto flex min-h-dvh w-full max-w-4xl flex-col gap-6 px-4 py-16 sm:px-6 lg:px-8">
			<BackLink fallback={{ to: '/writing', label: 'Writing' }} />
			<header className="flex flex-col gap-2">
				<div className="bg-foreground text-background inline-block w-fit rounded-lg px-3 py-1 text-sm">
					Demo · Immutable ledger
				</div>
				<h1 className="text-3xl font-bold tracking-tighter sm:text-4xl">
					Why editing the books destroys history
				</h1>
				<DemoCapsule href="/writing/immutable-ledger" className="my-0" />
				<p className="text-muted-foreground text-pretty">
					The trick isn&rsquo;t saving copies; it&rsquo;s that history is a{' '}
					<em>query</em>: the books store what changed, and any past state is
					recovered by asking for it as of a moment in time. Below, the same
					journal in two books — one built that way, one not.
				</p>
			</header>

			<section className="flex flex-col gap-4">
				<h2 className="text-xl font-semibold tracking-tight">
					Two ways to keep a book
				</h2>
				<p className="text-muted-foreground text-sm text-pretty">
					Same journal lines, same chart. One book remembers every state it ever
					held; the other forgets the moment you edit it. Play with both.
				</p>
				<div className="grid items-start gap-6 lg:grid-cols-2">
					<ImmutableBook />
					<MutableBook />
				</div>
				<p className="text-muted-foreground text-center text-sm">
					The axes are the same on both: <strong>X = entry date</strong>,{' '}
					<strong>Y = cumulative net (revenue − expense)</strong>. The only
					difference is what happens to the past when you change your mind.
				</p>
			</section>

			<section className="flex flex-col gap-4">
				<h2 className="text-xl font-semibold tracking-tight">
					What immutability buys you
				</h2>
				<p className="text-muted-foreground text-sm text-pretty">
					Everything below falls out of the same property: state is derived from
					an append-only log of events.
				</p>
				<div className="grid gap-4 text-left md:grid-cols-3">
					{FEATURES.map((f) => (
						<div key={f.title} className="bg-background rounded-lg border p-5">
							<h3 className="font-semibold">{f.title}</h3>
							<p className="text-muted-foreground mt-2 text-sm leading-6">
								{f.body}
							</p>
						</div>
					))}
				</div>
			</section>

			<p className="text-muted-foreground text-sm">
				The idea in full, without the toy —{' '}
				<Link
					to="/writing/declarative-accounting"
					className="text-foreground underline underline-offset-4"
				>
					Declarative accounting, explained
				</Link>
				; and where this property lets AI sit next to the books —{' '}
				<Link
					to="/writing/harness"
					className="text-foreground underline underline-offset-4"
				>
					the harness
				</Link>
				.
			</p>
		</main>
	)
}

const FEATURES: Array<{ title: string; body: string }> = [
	{
		title: 'Full audit trail',
		body: 'Every version is preserved, so you can answer “what did the books say on day N?” without a backup.',
	},
	{
		title: 'Corrections, not erasures',
		body: 'Fixing a mistake appends an adjusting entry. The original error — and its fix — both stay on the record.',
	},
	{
		title: 'Time-travel queries',
		body: 'Reconstruct any historical state on demand; reporting periods become a filter, not a snapshot you had to remember to take.',
	},
]
